/**
 * Customer Endpoint - matches PHP SDK Customer endpoint
 */

import { DreamscapeClient } from '../client';
import {
  CustomerExisting,
  CustomerCreate,
  CustomerUpdate,
  CustomerLoginLink,
  Collection,
} from '../types';

export class CustomerEndpoint {
  constructor(private client: DreamscapeClient) { }

  /**
   * Get all customers
   */
  async getAll(filters?: any): Promise<Collection<CustomerExisting>> {
    const params: Record<string, any> = {};
    if (filters) {
      Object.assign(params, filters);
    }

    return this.client.request<Collection<CustomerExisting>>('GET', 'customers', undefined, params);
  }

  /**
   * Get customer details
   */
  async getDetails(customerId: number): Promise<CustomerExisting> {
    if (customerId < 1) {
      throw new Error('Customer ID must be a positive integer');
    }

    return this.client.request<CustomerExisting>('GET', `customers/${customerId}`);
  }

  /**
   * Create customer
   */
  async create(data: CustomerCreate): Promise<CustomerExisting> {
    const requestData: any = {
      username: data.username,
      password: data.password,
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone: data.phone,
      address: data.address,
      city: data.city,
      state: data.state,
      post_code: data.postCode,
      country: data.country,
      country_code: data.countryCode,
      account_type: data.accountType,
    };

    if (data.mobile) requestData.mobile = data.mobile;
    if (data.businessName) requestData.business_name = data.businessName;
    if (data.businessNumber) requestData.business_number = data.businessNumber;
    if (data.businessNumberType) requestData.business_number_type = data.businessNumberType;

    return this.client.request<CustomerExisting>('POST', 'customers', requestData);
  }

  /**
   * Update customer
   */
  async update(customerId: number, data: CustomerUpdate): Promise<CustomerExisting> {
    if (customerId < 1) {
      throw new Error('Customer ID must be a positive integer');
    }

    const requestData: any = {};
    if (data.firstName) requestData.first_name = data.firstName;
    if (data.lastName) requestData.last_name = data.lastName;
    if (data.username) requestData.username = data.username;
    if (data.email) requestData.email = data.email;
    if (data.phone) requestData.phone = data.phone;
    if (data.address) requestData.address = data.address;
    if (data.city) requestData.city = data.city;
    if (data.state) requestData.state = data.state;
    if (data.postCode) requestData.post_code = data.postCode;
    if (data.country) requestData.country = data.country;
    if (data.password) requestData.password = data.password;

    return this.client.request<CustomerExisting>('PATCH', `customers/${customerId}`, requestData);
  }

  /**
   * Create login link for customer
   */
  async createLoginLink(customerId: number): Promise<CustomerLoginLink> {
    if (customerId < 1) {
      throw new Error('Customer ID must be a positive integer');
    }

    return this.client.request<CustomerLoginLink>('POST', `customers/${customerId}/login-link`);
  }

  /**
   * Terminate customer
   */
  async terminate(customerId: number): Promise<boolean> {
    if (customerId < 1) {
      throw new Error('Customer ID must be a positive integer');
    }

    await this.client.request('DELETE', `customers/${customerId}`);
    return true;
  }
}

